import type { CompRef } from '@wix/document-services-types';
import type { EditorElement } from '@wix/component-protocol/schema';
import { DISPLAY_GROUPS } from '@wix/component-protocol/schema';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

interface ISelectedArrayItemIndexEntry {
  index: number;
  editorPointer: EditorPointer<CompRef>;
}

const GLOBAL_SELECTED_ARRAY_ITEM_MAP = new Map<
  string,
  ISelectedArrayItemIndexEntry
>();

export function getArrayItemsCacheId(
  editorPointerAPI: EditorPointerServiceAPI,
  editorPointer: EditorPointer<CompRef>,
) {
  const documentPointer = editorPointerAPI.getDocumentPointer(editorPointer);
  const innerElementPath = editorPointerAPI.getInnerElementPath(editorPointer);

  return editorPointerAPI.getEditorPointerUniqueId(
    documentPointer,
    innerElementPath,
    { ignoreVariants: true },
  );
}

export function findArrayItemsSelectedIndexKey(
  displayGroups: EditorElement['displayGroups'],
  arrayItemsDisplayGroupKey?: string,
) {
  const arrayItemsDisplayGroups = Object.entries(displayGroups ?? {})
    .filter(([_displayGroupKey, _displayGroupValue]) => {
      return (
        _displayGroupValue.groupType === DISPLAY_GROUPS.GROUP_TYPE.arrayItems
      );
    })
    .filter(([displayGroupKey, _displayGroupValue]) =>
      arrayItemsDisplayGroupKey
        ? displayGroupKey === arrayItemsDisplayGroupKey
        : true,
    )
    .map(([_displayGroupKey, displayGroupValue]) => displayGroupValue);

  if (!arrayItemsDisplayGroups.length || arrayItemsDisplayGroups.length > 1) {
    return null;
  }

  return arrayItemsDisplayGroups[0].arrayItems?.selectedItemIndex;
}

export function getCachedArrayItemIndex(
  editorPointerAPI: EditorPointerServiceAPI,
  editorPointer: EditorPointer<CompRef>,
) {
  return (
    GLOBAL_SELECTED_ARRAY_ITEM_MAP.get(
      getArrayItemsCacheId(editorPointerAPI, editorPointer),
    )?.index ?? null
  );
}

export function setCachedArrayItemIndex(
  editorPointerAPI: EditorPointerServiceAPI,
  editorPointer: EditorPointer<CompRef>,
  index: number,
) {
  GLOBAL_SELECTED_ARRAY_ITEM_MAP.set(
    getArrayItemsCacheId(editorPointerAPI, editorPointer),
    {
      index,
      editorPointer,
    },
  );
}

export function deleteCachedArrayItemIndex(
  editorPointerAPI: EditorPointerServiceAPI,
  editorPointer: EditorPointer<CompRef>,
) {
  GLOBAL_SELECTED_ARRAY_ITEM_MAP.delete(
    getArrayItemsCacheId(editorPointerAPI, editorPointer),
  );
}

export function getAllCachedArrayItemEntries() {
  return Array.from(GLOBAL_SELECTED_ARRAY_ITEM_MAP.values());
}
